import React, { useEffect, useRef, useState } from 'react';
import './SelfBooking.css';

const TOURVISOR_SCRIPT_ID = 'tourvisor-init-script';
const TOURVISOR_SCRIPT_SRC = 'https://tourvisor.ru/module/init.js';
const TOURVISOR_DISABLED =
  String(process.env.REACT_APP_TOURVISOR_DISABLED || '').toLowerCase() === 'true';
const EMPTY_WIDGET_TIMEOUT_MS = 12000;
const FALLBACK_MESSAGE = 'Ой! Что-то пошло не так…. Скоро всё исправим';

const MODULE_SELECTORS = [
  '.tv-history.tv-moduleid-9995406',
  '.tv-search-form.tv-moduleid-9980543',
];

const runTourvisorInit = () => {
  try {
    if (typeof window.TV?.loadModules === 'function') {
      window.TV.loadModules();
      return;
    }
    if (typeof window.TV?.init === 'function') window.TV.init();
    if (typeof window.Tv?.init === 'function') window.Tv.init();
    if (typeof window.tourvisorInit === 'function') window.tourvisorInit();
  } catch (err) {
    console.warn('Tourvisor init error:', err);
  }
};

/** Сброс внутренних флагов, чтобы SPA-повторный заход снова подхватил контейнеры */
const resetTourvisorModuleState = () => {
  MODULE_SELECTORS.forEach((selector) => {
    const el = document.querySelector(selector);
    if (!el) return;
    el.innerHTML = '';
    Array.from(el.attributes)
      .map((attr) => attr.name)
      .filter((name) => name.startsWith('data-') || name.startsWith('tv-'))
      .forEach((name) => el.removeAttribute(name));
  });

  try {
    if (window.TV && window.TV.modules && typeof window.TV.modules === 'object') {
      Object.keys(window.TV.modules).forEach((key) => {
        delete window.TV.modules[key];
      });
    }
  } catch (_) {
    /* ignore */
  }
};

const loadTourvisorScript = () =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TOURVISOR_SCRIPT_ID);
    const headScript = document.querySelector('script[src*="tourvisor.ru/module/init.js"]');

    if (existingScript?.getAttribute('data-loaded') === 'true' || window.TV?.loadModules) {
      resolve();
      return;
    }

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Tourvisor script failed')), {
        once: true,
      });
      return;
    }

    // Скрипт из index.html (без нашего id) — ждём TV или догружаем свой
    if (headScript && !window.TV) {
      const waitId = window.setInterval(() => {
        if (window.TV?.loadModules || window.TV?.init) {
          window.clearInterval(waitId);
          resolve();
        }
      }, 100);
      window.setTimeout(() => {
        window.clearInterval(waitId);
        if (window.TV) resolve();
        else reject(new Error('Tourvisor script timeout'));
      }, 8000);
      return;
    }

    const script = document.createElement('script');
    script.id = TOURVISOR_SCRIPT_ID;
    script.src = TOURVISOR_SCRIPT_SRC;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = () => reject(new Error('Tourvisor script failed'));
    document.body.appendChild(script);
  });

const primaryWidgetsReady = () => {
  const search = document.querySelector('.tv-search-form.tv-moduleid-9980543');
  return Boolean(search && search.childElementCount > 0);
};

const TourvisorFallback = () => (
  <section className="self-booking-widget self-booking-widget--fallback" role="status">
    <p className="self-booking-fallback-text">{FALLBACK_MESSAGE}</p>
  </section>
);

const SelfBooking = () => {
  const timersRef = useRef([]);
  const observerRef = useRef(null);
  const widgetsRef = useRef(null);
  const [showFallback, setShowFallback] = useState(TOURVISOR_DISABLED);
  // Новый mount → новые DOM-узлы для модулей (важно для SPA)
  const [mountKey] = useState(() => Date.now());

  useEffect(() => {
    if (TOURVISOR_DISABLED) return undefined;

    let cancelled = false;
    const timers = timersRef.current;

    const trackTimer = (id) => {
      timers.push(id);
      return id;
    };

    const checkReady = () => {
      if (cancelled) return false;
      if (!primaryWidgetsReady()) return false;
      setShowFallback(false);
      return true;
    };

    const boot = () => {
      resetTourvisorModuleState();
      runTourvisorInit();
      [50, 200, 500, 1000, 2000, 4000].forEach((delay) => {
        trackTimer(
          window.setTimeout(() => {
            if (cancelled) return;
            if (!checkReady()) runTourvisorInit();
          }, delay)
        );
      });
    };

    loadTourvisorScript()
      .then(() => {
        if (!cancelled) boot();
      })
      .catch(() => {
        if (!cancelled) setShowFallback(true);
      });

    const container = widgetsRef.current;
    if (container && typeof MutationObserver !== 'undefined') {
      observerRef.current = new MutationObserver(() => {
        if (checkReady()) observerRef.current?.disconnect();
      });
      observerRef.current.observe(container, { childList: true, subtree: true });
    }

    trackTimer(
      window.setInterval(() => {
        if (cancelled) return;
        if (!checkReady()) runTourvisorInit();
      }, 1500)
    );

    trackTimer(
      window.setTimeout(() => {
        if (!cancelled && !primaryWidgetsReady()) setShowFallback(true);
      }, EMPTY_WIDGET_TIMEOUT_MS)
    );

    return () => {
      cancelled = true;
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
      timersRef.current = [];
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [mountKey]);

  if (TOURVISOR_DISABLED) {
    return (
      <main className="self-booking">
        <div className="self-booking-container">
          <TourvisorFallback />
        </div>
      </main>
    );
  }

  return (
    <main className="self-booking">
      <div className="self-booking-container">
        {showFallback && <TourvisorFallback />}

        <div
          ref={widgetsRef}
          className={`self-booking-widgets${showFallback ? ' self-booking-widgets--hidden' : ''}`}
          key={mountKey}
          aria-hidden={showFallback}
        >
          <section className="self-booking-widget">
            <div className="tv-history tv-moduleid-9995406" />
          </section>

          <section className="self-booking-widget">
            <div className="tv-search-form tv-moduleid-9980543" />
          </section>
        </div>
      </div>
    </main>
  );
};

export default SelfBooking;
